<script setup lang="ts">
  import type { AccessRole } from '@gat-ship/types'
  import type { ComboboxFieldProps } from '@gat-ship/vue-components'
  import { computed, onMounted, reactive, unref } from 'vue'
  import { useAccessRolesCollectionProvider } from '../providers/access-roles-collection-context'
  import { useAccessRoleOptions } from '../../composables/useAccessRoleOptions'
  import { globalUtils } from '@gat-ship/utils'
  import { IconPlus } from '@gat-ship/icons'
  import { ComboboxField, Separator } from '@gat-ship/vue-components'

  export interface SelectAccessRoleProps extends Omit<
    ComboboxFieldProps,
    'items' | 'modelValue'
  > {
    items?: AccessRole[]
    showAvatar?: boolean
    preSelectedAccessRole?: AccessRole
    fetchOnMount?: boolean
  }

  const props = withDefaults(defineProps<SelectAccessRoleProps>(), {
    showAvatar: true,
    fetchOnMount: false
  })

  const emit = defineEmits<{
    (e: 'click:add-access-role'): void
  }>()

  const eventsBound = reactive({
    addAccessRole: false
  })

  const accessRolesProvider = useAccessRolesCollectionProvider()

  const { buildOptions } = useAccessRoleOptions()

  function getDataSource() {
    return accessRolesProvider.dataSource
  }

  onMounted(() => {
    if (props.fetchOnMount) {
      accessRolesProvider.dataSource.fetchAccessRoles()
    }
    eventsBound.addAccessRole =
      globalUtils.functions.components.hasEmitListener('click:add-access-role')
  })

  const selectedUserId = defineModel<AccessRole['id'] | undefined>({
    required: true
  })

  const isOpen = defineModel<boolean>('open', { default: false })

  const _items = computed(() =>
    buildOptions(
      unref(getDataSource().accessRoles),
      props.preSelectedAccessRole
    )
  )
</script>
<template>
  <ComboboxField
    v-bind="props"
    v-model="selectedUserId"
    v-model:open="isOpen"
    :data-testid="props.dataTestid ?? 'select-agency-group-input'"
    :items="_items"
    @update:open.once="
      () => {
        getDataSource().fetchAccessRoles()
      }
    "
  >
    <template #item="{ item }">
      <div class="flex min-w-[200px] grow items-center gap-2">
        <div>{{ item.metaData.name }}</div>
      </div>
    </template>
    <template #append-content-item>
      <separator></separator>
      <div
        class="data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground outline-hidden relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-3 text-sm data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0"
        @click="emit('click:add-access-role')"
      >
        <div class="text-foreground text-sm">Add Role</div>
        <IconPlus class="ml-auto size-3.5" :stroke-width="2" />
      </div>
    </template>
  </ComboboxField>
</template>
